import { useEffect, useState } from 'react';
import { invokeBackendRoute } from '@/backend/invoke';

type AuthState = 'unknown' | 'authenticated' | 'unauthenticated';

/**
 * Gets a cookie value by name
 */
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return decodeURIComponent(parts.pop()?.split(';').shift() || '');
  }
  return null;
};

export const useAuthRedirect = () => {
  const [authState, setAuthState] = useState<AuthState>('unknown');

  useEffect(() => {
    let isMounted = true;
    let redirecting = false;

    const checkAndRedirect = async (userEmail: string) => {
      if (redirecting) return;

      try {
        // Check if this user belongs to a firms tenant via the public lookup route.
        const result = await invokeBackendRoute<
          { tenantId: string },
          { email: string }
        >('findEmployeeTenant', { email: userEmail });

        if (result?.tenantId) {
          // User is a firms user, redirect them to the firms app immediately
          redirecting = true;
          if (isMounted) {
            setAuthState('authenticated');
          }
          // Use replace instead of href to prevent back button issues
          window.location.replace('https://firms.practist.io/matters');
          return true;
        }
      } catch (error) {
        // If the function call fails (e.g., user is not a firms user),
        // that's okay - they can stay on the public app
        console.log('[useAuthRedirect] User is not a firms user:', error);
      }
      return false;
    };

    // Check for cross-domain auth cookie (set by firms.practist.io)
    console.log('[useAuthRedirect] All cookies:', document.cookie);
    const authEmail = getCookie('practist_auth_email');
    console.log('[useAuthRedirect] Found auth email from cookie:', authEmail);

    if (authEmail) {
      // Found auth cookie, check if user is a firms user and redirect
      console.log('[useAuthRedirect] Checking if user is a firms user...');
      checkAndRedirect(authEmail).then((redirected) => {
        console.log('[useAuthRedirect] Redirect result:', redirected);
        if (!redirected && isMounted) {
          setAuthState('unauthenticated');
        }
      });
    } else {
      // No auth cookie found, show public app
      console.log('[useAuthRedirect] No auth cookie found, showing public app');
      if (isMounted) {
        setAuthState('unauthenticated');
      }
    }

    return () => {
      isMounted = false;
    };
  }, []);

  return authState;
};
